import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FC, Suspense, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "sonner";
import { useWalletObserver, useWalletLoadingState } from "@sundaeswap/wallet-lite";

import { ProvidersWrapper } from "./components/ProvidersWrapper";
import { RevealOverlay } from "./components/RevealOverlay";
import { GummiwormBalance } from "./components/GummiwormBalance";
import { Header } from "./components/Header";
import { WalletBalance } from "./components/WalletBalance";
import { DepositOverlay } from "./components/DepositOverlay";
import { IntroStep } from "./components/DepositOverlay/IntroStep";
import { TradingView } from "./components/TradingView";
import { useGummiwormContext } from "./context/GummiwormContext";
import { useNetworkCheck } from "./hooks/useNetworkCheck";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
    },
  },
});

const ErrorFallback = ({ error }: { error: Error }) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="p-6 rounded-xl bg-sr-surface max-w-sm mx-4">
      <h2 className="text-lg font-medium text-white mb-3">
        Something went wrong
      </h2>
      <pre className="text-xs text-sr-salmon bg-sr-salmon/10 p-3 rounded-lg overflow-auto">
        {error.message}
      </pre>
    </div>
  </div>
);

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-6 h-6 rounded-full border-2 border-sr-lemon-muted border-t-transparent animate-spin" />
  </div>
);

const MainContent: FC = () => {
  const { activeWallet } = useWalletObserver();
  const { ready: walletReady } = useWalletLoadingState();

  const isConnected = !!activeWallet;
  const { mismatch: networkMismatch } = useNetworkCheck();
  const { accounts, myAccountId, accountsReady, tradingEnabled } = useGummiwormContext();
  const myAccount = accounts.find((a) => a.accountId === myAccountId);

  // Determine initial view: wait for wallet + accounts to settle before deciding.
  const [view, setView] = useState<"deposit" | "trade" | null>(null);
  const hasAutoSwitched = useRef(false);
  const minDelayPassed = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      minDelayPassed.current = true;
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!walletReady) return;
    if (isConnected && !accountsReady) return;

    const timer = setTimeout(() => {
      if (myAccount && tradingEnabled && !hasAutoSwitched.current) {
        hasAutoSwitched.current = true;
        setView("trade");
      } else if (view === null) {
        setView(isConnected && myAccount && tradingEnabled ? "trade" : "deposit");
      }
    }, minDelayPassed.current ? 0 : 300);
    return () => clearTimeout(timer);
  }, [walletReady, isConnected, accountsReady, myAccount, tradingEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reveal animation: when trading flips on and user has an account
  const [revealing, setRevealing] = useState(false);
  const [revealWidgets, setRevealWidgets] = useState(false);
  const wasTradingDisabled = useRef(!tradingEnabled);

  useEffect(() => {
    // Only trigger reveal if we were on the deposit screen and trading just got enabled
    if (tradingEnabled && wasTradingDisabled.current && myAccount && view === "deposit") {
      wasTradingDisabled.current = false;
      setRevealing(true);
    } else if (tradingEnabled && myAccount && !hasAutoSwitched.current) {
      // Direct switch (page loaded with trading already enabled)
      hasAutoSwitched.current = true;
      setView("trade");
    }
    if (!tradingEnabled) {
      wasTradingDisabled.current = true;
    }
  }, [tradingEnabled, myAccount, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRevealSwitch = () => {
    // Called while overlay is opaque — switch to exchange behind it, widgets hidden
    hasAutoSwitched.current = true;
    setRevealWidgets(true);
    setView("trade");
  };

  const handleRevealComplete = () => {
    // Overlay gone — widgets are already animating in from the render
    setRevealing(false);
    setTimeout(() => setRevealWidgets(false), 2500);
  };
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositShellScaled, setDepositShellScaled] = useState(false);
  const [viewRequestId, setViewRequestId] = useState<string | null>(null);

  const openDepositFlow = (requestId?: string | null) => {
    if (networkMismatch) return;
    setDepositShellScaled(true);
    setViewRequestId(requestId ?? null);
    setDepositOpen(true);
  };

  const handleDepositCloseStart = () => {
    setDepositShellScaled(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <RevealOverlay active={revealing} onSwitch={handleRevealSwitch} onComplete={handleRevealComplete} />
      <div
        className={`sr-app-shell ${depositShellScaled ? "sr-app-shell-overlay-open" : ""}`}
      >
        <Header view={view ?? "deposit"} onViewChange={setView} networkMismatch={networkMismatch} tradingEnabled={tradingEnabled} hideLogo={revealing} />

        {view === null ? (
          <main className="flex-1 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full border-2 border-sr-lemon-muted border-t-transparent animate-spin" />
          </main>
        ) : view === "trade" ? (
          <main className="flex-1 flex flex-col bg-[#170A10] px-4 pb-4 lg:px-6">
            <TradingView onDeposit={() => openDepositFlow()} depositDisabled={!!networkMismatch} revealWidgets={revealWidgets} />
          </main>
        ) : (
          <main className="flex-1 flex flex-col items-center px-4 pb-6 sm:pb-12">
            {!isConnected ? (
              <div className="flex w-full max-w-[420px] flex-1 flex-col items-center justify-center gap-8 py-10">
                <IntroStep
                  isConnected={false}
                  onBegin={() => openDepositFlow()}
                  title={`Welcome to ${tradingEnabled ? "Sugar Rush" : "Gummiworm"}`}
                />
              </div>
            ) : (
              <div className="w-full max-w-md flex flex-col gap-6">
                <WalletBalance onDeposit={() => openDepositFlow()} depositDisabled={!!networkMismatch} />

                <GummiwormBalance
                  onViewDeposit={(id) => {
                    openDepositFlow(id);
                  }}
                />

                {myAccount && !tradingEnabled && (
                  <div className="sr-card p-5 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="w-4 h-4 rounded-full border-2 border-sr-lemon-muted border-t-transparent animate-spin" />
                      <span className="text-sm font-medium text-sr-lemon-muted">
                        Any minute now...
                      </span>
                    </div>
                    <p className="text-xs text-sr-neutral-500">
                      Your funds are on the L2. Stay tuned — something's coming.
                    </p>
                  </div>
                )}
              </div>
            )}
          </main>
        )}
      </div>

      <DepositOverlay
        open={depositOpen}
        onCloseStart={handleDepositCloseStart}
        onClose={() => {
          setDepositOpen(false);
          setViewRequestId(null);
        }}
        viewRequestId={viewRequestId}
      />

      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "var(--color-sr-surface)",
            color: "var(--color-sr-oat-milk)",
            border: "1px solid var(--color-sr-border-subtle)",
          },
        }}
      />
    </div>
  );
};

export const App: FC = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<LoadingFallback />}>
        <QueryClientProvider client={queryClient}>
          <ProvidersWrapper>
            <MainContent />
          </ProvidersWrapper>
        </QueryClientProvider>
      </Suspense>
    </ErrorBoundary>
  );
};
